import React, { useCallback, useEffect, useRef, useState, useContext, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import PixelInput from '../general-components/pixel-input';
import { SelectGroupOption } from '../general-components/select-group';
import SelectSmall from '../general-components/select-small';
import Icon, { ICONS, COLORS } from '../general-components/icon';

import { MediaData } from '../../stores/reducers/media';
import DurationPicker from '../general-components/duration-picker';
import { GreenroomState } from '../../stores/types/types';
import MediaAudio from '../general-components/media-audio';

import { getVideoDuration } from '../../utils/media';
import { ResourcePoolContext } from '../../hooks/use-resource-pool';
import * as MediaActions from '../../stores/actions/media';
import { useGetMediaControls } from '../../contexts/media-controls';
import { videoActionCommands } from '../../utils/video-machine';

import { setActiveSubTab, setActiveTab, setRightDrawerOpen } from '../../stores/actions/tabs';
import { TABS } from '../../stores/types/tabs';
import { MediaTabOptionTypes } from '../../stores/reducers/session';
import { setMediaFilters } from '../../stores/actions/user-interface';
import { MEDIA_FILTER_TYPES, MEDIA_SORT_TYPES } from '../../types/constants';
import useMediaItemMemos from '../../hooks/use-media-memos';
import { CreateMediaFromGenerateContentSound } from '../../connection/media'
import { MediaItem } from '../../stores/reducers/media';

import IconToggle from '../general-components/icon-toggle';
import DropdownButton, { OptionButton, OptionIconContainer, OptionLabel } from '../general-components/dropdown-button';
import { ReactComponent as TellyMagicSVG } from '../../images/telly-magic.svg';
import { ShotFeed, FEED_TYPES } from '../../stores/reducers/shots';
import useShots from '../../hooks/use-shots';

const fadeOptions: SelectGroupOption[] = [
    { label: '0s', value: 0 },
    { label: '1s', value: 1 },
    { label: '2s', value: 2 },
    { label: '3s', value: 3 },
    { label: '4s', value: 4 },
    { label: '5s', value: 5 },
    { label: '6s', value: 6 },
];

const doFireSync = false;
const { PLAY, PAUSE, TIMECHANGE, SCRUB, STOPSCRUBBING } = videoActionCommands;

export default function AudioSettings() {
    const dispatch = useDispatch();
    const { pushShotFeedToCurrentShot } = useShots();
    
    const {
        currentTemplate,
        currentTemplateMediaItem,
        currentAudioMediaItem,
    } = useMediaItemMemos();

    const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
    const session = useSelector((state: GreenroomState) => state.SessionReducer.session);
    const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
    const meetingId = useSelector((state: GreenroomState) => state.SessionReducer.meeting?.meeting_id);   

	const generatedContentTemplates = useSelector((state: GreenroomState) => state.GeneratedContentReducer.contentTemplates);

    const mediaPages = useSelector((state: GreenroomState) => state.MediaReducer.mediaPages);
    const selectedPageItem = useMemo(
        () => mediaPages.find((p) => p.mediaId === currentAudioMediaItem?.id),
        [mediaPages, currentAudioMediaItem],
    );
    const [volume, setVolume] = useState(Math.floor((selectedPageItem?.volume ?? 1) * 100));

    const [properties, setProperties] = useState(currentAudioMediaItem?.media_data ?? {
        fadeIn: 0,
        fadeOut: 0,
        offsetStartSecond: 0,
        offsetEndSecond: 0,
        loop: false,
    } as MediaData);

    const selectedFadeIn = fadeOptions.find((option) => option.value === properties.fadeIn);
    const selectedFadeOut = fadeOptions.find((option) => option.value === properties.fadeOut);
    const previewElRef = useRef<HTMLAudioElement>(null);

    const mediaURL = useMemo(
        () => (currentAudioMediaItem ? currentAudioMediaItem.media_data.image_array[0] : ''),
        [currentAudioMediaItem],
    );

    const resourcePool = useContext(ResourcePoolContext);
    const [duration, setDuration] = useState<number | null>(null);

    // Canvas media controls for syncing play/pause/scrub
    const templateControl = useGetMediaControls(String(currentTemplateMediaItem?.id ?? ''));
    const audioCanvasControl = useGetMediaControls(String(currentAudioMediaItem?.id ?? ''));
    const offsetDuration = useMemo(() => {
        if (duration) {
            return duration - (properties.offsetEndSecond ?? 0);
        } else {
            return 0;
        }
      }, [duration, properties.offsetEndSecond]);
    
    const [replaceAudioOptionsOpen, setReplaceAudioOptionsOpen] = useState<boolean>(false);

    // Display-only callbacks: forward play/pause/seek to canvas template control
    const handleDisplayPlayPause = useCallback(() => {
        if (!templateControl?.send) return;
        const isPaused = templateControl.state?.paused ?? true;
        if (isPaused) {
            templateControl.send(PLAY, { doFireSync, from: 'audio-settings' });
            if (templateControl.state?.wasEnded && !audioCanvasControl?.state?.wasEnded) {
                audioCanvasControl?.send?.(TIMECHANGE, {
                    currentTime: 0,
                    duration: audioCanvasControl?.state?.duration ?? 0,
                    doFireSync,
                    from: 'audio-settings',
                });
            }
        } else {
            templateControl.send(PAUSE, { doFireSync, from: 'audio-settings' });
        }
    }, [templateControl, audioCanvasControl]);

    const handleDisplayScrubStart = useCallback(() => {
        if (!templateControl?.send) return;
        templateControl.send(SCRUB, {
            currentTime: templateControl.state?.elapsed ?? 0,
            duration: templateControl.state?.duration ?? 0,
            from: 'audio-settings',
        });
    }, [templateControl]);

    const handleDisplayScrub = useCallback((time: number) => {
        if (!templateControl?.send || !isFinite(time)) return;
        templateControl.send(TIMECHANGE, {
            currentTime: time,
            duration: templateControl.state?.duration ?? 0,
            doFireSync,
            from: 'audio-settings',
        });
    }, [templateControl]);

    const handleDisplayScrubEnd = useCallback((time: number) => {
        if (!templateControl?.send || !isFinite(time)) return;
        const wasEnded = templateControl.state?.wasEnded ?? false;
        templateControl.send(STOPSCRUBBING, {
            currentTime: time,
            duration: templateControl.state?.duration ?? 0,
            doFireSync,
            from: 'audio-settings',
        });
        if (wasEnded) {
            templateControl?.send?.(PLAY, { doFireSync, from: 'audio-settings' });
        }
    }, [templateControl]);

    const handleDisplayVersionSelect = useCallback(() => {
        // not sure why this works, might be related to loading in the audio into the element
        const controlChange = setTimeout(() => {
            if (!templateControl?.send) return;
            templateControl.send(SCRUB, { currentTime: 0, doFireSync, from: 'audio-settings-version-select' });
            templateControl.send(STOPSCRUBBING, {doFireSync});
            templateControl?.send?.(PLAY, { doFireSync, from: 'audio-settings-version-select' });
        }, 300)

        return () => {
            clearTimeout(controlChange)
        }
    }, [templateControl]);

    useEffect(() => {
        const fetchDuration = async () => {
            if (resourcePool) {
                const videoElm = document.createElement('video');
                videoElm.preload = 'metadata';
                videoElm.crossOrigin = 'anonymous';
        
                const result = await getVideoDuration(
                    mediaURL,
                    currentAudioMediaItem?.type ?? '',
                    videoElm,
                    resourcePool,
                );
                setDuration(result);
            }
        };

        fetchDuration();
    }, []);

    useEffect(() => {
        if (currentAudioMediaItem?.media_data) {
            setProperties(currentAudioMediaItem?.media_data);
        }
    }, [currentAudioMediaItem?.media_data]);

    // Auto-create PagedMediaItem if none exists for current audio media
    useEffect(() => {

        if (currentAudioMediaItem && !selectedPageItem && token && meeting?.meeting) {
            const newPageItem = {
                mediaId: currentAudioMediaItem.id,
                meeting: meeting.meeting,
                url: currentAudioMediaItem.images,
                volume: 1.0,
                muted: false,
                duration: null,
            };
            
            dispatch(MediaActions.saveMediaPage(
                token,
                meeting.meeting,
                newPageItem,
                session || '',
            ));
        }
    }, [currentAudioMediaItem, selectedPageItem, token, meeting, session]);

    let lastTimeoutSave:number = 0;
    const mediaDataUpdate = useCallback((newProperties: MediaData) => {
        setProperties(newProperties);

        if (currentAudioMediaItem && token) {
            currentAudioMediaItem.media_data = newProperties;

            if (lastTimeoutSave)
                clearTimeout(lastTimeoutSave);

            lastTimeoutSave = setTimeout((value: string) => {
                dispatch(MediaActions.saveMediaItem(token, currentAudioMediaItem, undefined));
            }, 200);
        }
    }, [currentAudioMediaItem]);
    const pageItemUpdate = useCallback((value: number) => {
        if (selectedPageItem && token && session && meeting) {
            selectedPageItem.volume = value;

            if (lastTimeoutSave)
                clearTimeout(lastTimeoutSave);

            lastTimeoutSave = setTimeout((value: string) => {
                dispatch(MediaActions.saveMediaPage(
                    token,
                    meeting.meeting,
                    selectedPageItem,
                    session,
                ));
            }, 200);
        }
    }, [selectedPageItem]);
    
    useEffect(() => {
        setVolume(Math.floor((selectedPageItem?.volume ?? 1) * 100));
    }, [selectedPageItem?.volume]);

    ///////////

    function onVolumeSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (selectedPageItem) {
            const value = Number(e.target.value);
            setVolume(value);
            pageItemUpdate(value / 100);
        }
    }

    function onVolumeTextChange(value: number) {        
        if (selectedPageItem) {
            setVolume(value);
            pageItemUpdate(value / 100);
        }
    }

    const onStartsChange = (valueSeconds:number) => {
        if (duration)
            valueSeconds = Math.min(duration, valueSeconds);

        const newProperties = { ...properties, offsetStartSecond: valueSeconds };
        mediaDataUpdate(newProperties);
    };

    const onEndsChange = (valueSeconds:number) => {
        valueSeconds = Math.max(valueSeconds, properties.offsetStartSecond ?? 0);
        valueSeconds = Math.max((duration ?? 0) - valueSeconds, 0);
        const newProperties = { ...properties, offsetEndSecond: valueSeconds };
        mediaDataUpdate(newProperties);
    };

    function onFadeInSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
        const newProperties = { ...properties, fadeIn: Number(e.target.value) };
        mediaDataUpdate(newProperties);
    }

    function onFadeInSelectChange(value: number) {
        const newProperties = { ...properties, fadeIn: value };
        mediaDataUpdate(newProperties);
    }

    function onFadeOutSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
        const newProperties = { ...properties, fadeOut: Number(e.target.value) };
        mediaDataUpdate(newProperties);
    }
    
    function onFadeOutSelectChange(value: number) {
        const newProperties = { ...properties, fadeOut: value };
        mediaDataUpdate(newProperties);
    }

    function onLoopChange(value:boolean) {
        const newProperties = { ...properties, loop: value };
        mediaDataUpdate(newProperties);
    }

    async function onResetClick(event: React.MouseEvent<HTMLButtonElement>) {
        if (token && meetingId && currentTemplate?.stylePack) {

            let targetDuration = Math.floor(currentTemplateMediaItem?.media_data?.duration_map?.[0] || currentTemplate.data?.duration);
            let matchingItems;
            if (targetDuration) {
                //attempt to find a audio version that matches duration
                matchingItems = generatedContentTemplates.filter(x => x.stylePack == currentTemplate.stylePack && x.contentType && x.contentType.toString() == "audio_version" && x.data?.config?.duration == targetDuration);

                if (matchingItems?.length == 0) {
                    //attempt to find a audio version (loopable) that matches duration
                    matchingItems = generatedContentTemplates.filter(x => x.stylePack == currentTemplate.stylePack && x.contentType && x.contentType.toString() == "audio_version_loopable" && x.data?.config?.duration == targetDuration);
                }

                if (matchingItems?.length == 0) {
                    //since a version is not found, find a whole track instead
                    matchingItems = generatedContentTemplates.filter(x => x.stylePack == currentTemplate.stylePack && x.contentType && x.contentType.toString() == 'audio');
                }

                if (matchingItems?.length) {
                    let audioItem = matchingItems[0];
                    if (audioItem && audioItem.videoUrl) {
                        let newMediaItem = (await CreateMediaFromGenerateContentSound(token, meetingId, audioItem.type, audioItem.data?.id)) as MediaItem;
                        let newFeed = {
                            position: 0,
                            feedType: FEED_TYPES.MEDIA_AUDIO,
                            media: newMediaItem.id,
                            audioOnly: true,
                        } as ShotFeed;
                        await pushShotFeedToCurrentShot(newFeed, true);
                    }
                }
            }
        }
    }

    function onReplaceClick(event: React.MouseEvent<HTMLButtonElement>) {
        dispatch(setRightDrawerOpen(true));
        dispatch(setActiveTab(TABS.MEDIA));
        dispatch(setActiveSubTab(TABS.MEDIA, MediaTabOptionTypes.Files));
        dispatch(setMediaFilters({
            selectedFilterType: MEDIA_FILTER_TYPES.AUDIO,
            selectedSortType: MEDIA_SORT_TYPES.NONE,
        }));
    }

    return (
        currentAudioMediaItem ?
        <div className="settings settings-container audio-media-settings">
            <div className="grid-layout">
                <div className="full-width">
                    <MediaAudio
                        id={`media-raw-${currentAudioMediaItem?.id}`}
                        mediaItem={currentAudioMediaItem ?? undefined}
                        elapsed={0}
                        duration={duration ?? 0}
                        loop={currentAudioMediaItem?.media_data.loop}
                        displayOnly={true}
                        externalPaused={templateControl?.state?.paused ?? true}
                        externalCurrentTime={templateControl?.state?.elapsed ?? 0}
                        externalDuration={templateControl?.state?.duration ?? 0}
                        onDisplayPlayPause={handleDisplayPlayPause}
                        onDisplayScrubStart={handleDisplayScrubStart}
                        onDisplayScrub={handleDisplayScrub}
                        onDisplayScrubEnd={handleDisplayScrubEnd}
                        onDisplayVersionSelect={handleDisplayVersionSelect}
                        showVersions={currentAudioMediaItem.provider == 'epidemic sound'}
                    />
                </div>
                
                {(!(currentAudioMediaItem.provider === 'epidemic sound')) && (
                <div style={{ gridColumn: 'span 12' }} className="grid-layout">
                    <div className="full-width" style={{ display: 'flex' }}>
                        <div style={{ flexGrow: 1, display: 'flex' }}>
                            <label style={{ margin: 'auto 0' }}>Starts</label>
                            <div style={{ flexGrow: 1 }}></div>
                            <DurationPicker
                                initialDuration={properties.offsetStartSecond}
                                getDurationCB={onStartsChange}
                                isInputDisabled={false}
                                tooltipMessage={''}
                                allowZero={true}
                            />
                        </div>
                        <div style={{ width: '48px' }}></div>
                        <div style={{ flexGrow: 1, display: 'flex' }}>
                            <label style={{ margin: 'auto 0' }}>Ends</label>
                            <div style={{ flexGrow: 1 }}></div>
                            <DurationPicker
                                initialDuration={offsetDuration}
                                getDurationCB={onEndsChange}
                                isInputDisabled={false}
                                tooltipMessage={''}
                                allowZero={true}
                            />
                        </div>
                    </div>
                </div>
                )}

                {(!(currentAudioMediaItem.provider === 'epidemic sound')) && (
                <div style={{ gridColumn: 'span 12' }} className="grid-layout">
                    <div style={{display: 'flex', gridColumn: 'span 12'}}>
                    <label style={{flexGrow: 1, margin: 'auto 0'}} className="subsection-title">Loop</label>
                        <IconToggle
                            onIcon={ICONS.LOOP_TRACK_ON}
                            offIcon={ICONS.LOOP_TRACK_OFF}
                            size={16}
                            checked={properties?.loop ?? false}
                            onChange={onLoopChange}
                        />
                    </div>
                    <div className="breaker full-width"></div>
                </div>
                )}

                {/* Fade In row, 3 parts */}
                <div className="third-width" style={{ margin: 'auto 0' }}>
                    <label>Fade In</label>
                </div>
                <div className="slider-input half-width" style={{ margin: 'auto 0' }}>
                    <input
                        onChange={onFadeInSliderChange}
                        type="range"
                        min="0"
                        max={fadeOptions.length - 1}
                        value={properties.fadeIn ?? 0}
                        />
                </div>
                <div className="third-width inputs">
                    <SelectSmall
                        options={fadeOptions}
                        label={selectedFadeIn?.label || fadeOptions[0].label}
                        selected={selectedFadeIn || fadeOptions[0]}
                        dropdown={true}
                        onChange={onFadeInSelectChange}
                        enclosed={true}
                    />
                </div>

                {/* Fade Out row, 3 parts */}
                <div className="third-width" style={{ margin: 'auto 0' }}>
                    <label>Fade Out</label>
                </div>
                <div className="slider-input half-width" style={{ margin: 'auto 0' }}>
                    <input
                        onChange={onFadeOutSliderChange}
                        type="range"
                        min="0"
                        max="6"
                        value={properties.fadeOut ?? 0}
                        />
                </div>
                <div className="third-width inputs">
                    <SelectSmall
                        options={fadeOptions}
                        label={selectedFadeOut?.label || fadeOptions[0].label}
                        selected={selectedFadeOut || fadeOptions[0]}
                        dropdown={true}
                        onChange={onFadeOutSelectChange}
                        enclosed={true}
                    />
                </div>

                <div className="breaker full-width"></div>
                {/* Volume row, 3 parts */}
                <div className="third-width" style={{ margin: 'auto 0' }}>
                    <label>Volume</label>
                </div>
                <div className="slider-input half-width" style={{ margin: 'auto 0' }}>
                    <Icon name={ICONS.VOLUME_0} color={COLORS.TEXT_GRAY_200} size={20}></Icon>
                    <input
                        onChange={onVolumeSliderChange}
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={volume}
                        />
                    <Icon name={ICONS.VOLUME_3} color={COLORS.TEXT_GRAY_200} size={20}></Icon>
                </div>
                <div className="third-width inputs">
                    <PixelInput
                        onChange={onVolumeTextChange}
                        value={volume}
                        unit={"%"}
                        min={0}
                        max={100}
                        />
                </div>

            </div>
            <div style={{ flexGrow: 1, display: 'flex' }}></div>
            <div className="grid-layout footer" style={{ gridTemplateColumns: '1fr' }}>
                <DropdownButton 
                    label="Replace Audio"
                    containerClassName="replace-audio-context-menu"
                >
                    <OptionButton onClick={onReplaceClick}>
                        <OptionIconContainer>
                            <Icon name={ICONS.UPLOAD} color={COLORS.WHITE} size={12} />
                        </OptionIconContainer>
                        <OptionLabel>Upload New</OptionLabel>
                    </OptionButton>
                    <OptionButton onClick={onResetClick}>
                        <OptionIconContainer>
                            <Icon name={ICONS.REDO} color={COLORS.WHITE} size={12} />
                        </OptionIconContainer>
                        <OptionLabel>Reset to Default</OptionLabel>
                    </OptionButton>
                </DropdownButton>
            </div>
            <div id="replace-audio-portal"></div>
            <div style={{ display: 'none' }}>
                <MediaAudio
                    id={`media-preview-${currentAudioMediaItem?.id}`}
                    mediaItem={currentAudioMediaItem ?? undefined}
                    elapsed={0}
                    duration={duration ?? 0}
                    loop={currentAudioMediaItem?.media_data.loop}
                    muted={false}
                    shownVolume={volume / 100}
                    ref={previewElRef}
                    specialEffects={true}
                />
            </div>
        </div> : <div className='settings settings-container audio-media-settings-empty'>
            <div style={{display: 'flex' }}>
                <div style={{flexGrow: 1}}></div>
                <TellyMagicSVG className="tellymagic"
                    width={288} 
                    height={209}
                />
                <div style={{flexGrow: 1}}></div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{flexGrow: 1}}></div>
                <h4>Tune it up!</h4>
                <p style={{flexGrow: 1}}></p>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{flexGrow: 1}}></div>
                <div>Upload an audio file to pair with your asset</div>
                <div style={{flexGrow: 1}}></div>
            </div>
            <div style={{display: 'flex'}} className="actions">
                <div style={{flexGrow: 1}}></div>
                <button className="background-grayscale-600 grayscale-200" onClick={onResetClick}>
                    <Icon name={ICONS.REDO} color={COLORS.TEXT_GRAY_200} size={16}></Icon>
                    <span>Reset to Default</span>
                </button>
                <button className="background-pink-lemonade" onClick={onReplaceClick}>
                    <Icon name={ICONS.UPLOAD} color={COLORS.TEXT_GRAY_1200} size={12}></Icon>
                    <span>Upload Audio</span>
                </button>
                <div style={{flexGrow: 1}}></div>
            </div>
        </div>
    )
}